import React, { useEffect, useRef, useState } from 'react';
import { Mail, Phone, MapPin, Send, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Contact.css';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const Contact = () => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
    });
    const sectionRef = useRef(null);
    const titleRef = useRef(null);
    const formRef = useRef(null);
    const infoRef = useRef(null);

    useEffect(() => {
        // Animate title
        if (titleRef.current) {
            gsap.fromTo(
                titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: 'top 80%',
                        toggleActions: 'play none none none',
                    },
                }
            );
        }

        // Animate form and info
        if (formRef.current && infoRef.current) {
            gsap.fromTo(
                formRef.current,
                { opacity: 0, x: -50 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.8,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: formRef.current,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                    },
                }
            );

            gsap.fromTo(
                infoRef.current,
                { opacity: 0, x: 50 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.8,
                    ease: 'power3.out',
                    scrollTrigger: {
                        trigger: infoRef.current,
                        start: 'top 85%',
                        toggleActions: 'play none none none',
                    },
                }
            );
        }

        return () => {
            ScrollTrigger.getAll().forEach((trigger) => {
                if (sectionRef.current && trigger.vars.trigger === sectionRef.current) {
                    trigger.kill();
                }
            });
        };
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Handle form submission
        console.log('Form submitted:', formData);
        alert(t('contact.form.success'));
        setFormData({ name: '', email: '', phone: '', message: '' });
    };

    return (
        <section id="contact" ref={sectionRef} className="contact">
            <div className="contact-container">
                <div className="contact-header">
                    <div className="section-badge">{t('contact.badge')}</div>
                    <h2 ref={titleRef} className="section-title">
                        {t('contact.title')}
                    </h2>
                    <p className="section-description">
                        {t('contact.subtitle')}
                    </p>
                </div>
                <div className="contact-content">
                    <form ref={formRef} className="contact-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="name">{t('contact.form.name')}</label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                placeholder={t('contact.form.name')}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">{t('contact.form.email')}</label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                placeholder={t('contact.form.email')}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">{t('contact.form.phone')}</label>
                            <input
                                type="tel"
                                id="phone"
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                placeholder={t('contact.info.phoneValue')}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="message">{t('contact.form.message')}</label>
                            <textarea
                                id="message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                required
                                rows="6"
                                placeholder={t('contact.form.message')}
                            />
                        </div>
                        <button type="submit" className="btn-primary btn-large btn-submit">
                            {t('contact.form.send')}
                            <Send size={20} />
                        </button>
                    </form>
                    <div ref={infoRef} className="contact-info">
                        <div className="info-card">
                            <div className="info-icon">
                                <Phone size={24} />
                            </div>
                            <div className="info-content">
                                <h3>{t('contact.info.phone')}</h3>
                                <p>{t('contact.info.phoneValue')}</p>
                            </div>
                        </div>
                        <div className="info-card">
                            <div className="info-icon">
                                <Mail size={24} />
                            </div>
                            <div className="info-content">
                                <h3>{t('contact.info.email')}</h3>
                                <p>{t('contact.info.emailValue')}</p>
                            </div>
                        </div>
                        <div className="info-card">
                            <div className="info-icon">
                                <MapPin size={24} />
                            </div>
                            <div className="info-content">
                                <h3>{t('contact.info.address')}</h3>
                                <p>{t('contact.info.addressValue')}</p>
                                <a 
                                    href="https://maps.app.goo.gl/CvGYW5aVvxTo5u7A8" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ 
                                        color: '#2563eb', 
                                        textDecoration: 'none', 
                                        marginTop: '8px', 
                                        display: 'inline-block',
                                        fontWeight: '500'
                                    }}
                                >
                                    View on Google Maps →
                                </a>
                            </div>
                        </div>
                        <div className="info-card">
                            <div className="info-icon">
                                <Clock size={24} />
                            </div>
                            <div className="info-content">
                                <h3>{t('contact.hours.title')}</h3>
                                <p>Daily: 5:30 PM - 7:30 PM</p>
                                <p>Sunday: On Appointment Only</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="contact-map" style={{ marginTop: '3rem', width: '100%', maxWidth: '1000px', marginLeft: 'auto', marginRight: 'auto' }}>
                    <div style={{ 
                        width: '100%', 
                        height: '400px', 
                        borderRadius: '12px', 
                        overflow: 'hidden', 
                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                        position: 'relative'
                    }}>
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3668.1234567890123!2d85.3839144!3d23.3950132!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f4e3619d65d6e3%3A0x68917194ecbeb03c!2sISHITA%20DENTAL%20CLINIC-%20Best%20Dental%20Clinic%20in%20Ranchi!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin&q=Renu+Complex+Booty+More+Ranchi"
                            width="100%"
                            height="400"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Ishita Dental Clinic Location - Renu Complex, Booty More"
                        ></iframe>
                        <div style={{ 
                            marginTop: '1rem', 
                            textAlign: 'center' 
                        }}>
                            <a 
                                href="https://maps.app.goo.gl/CvGYW5aVvxTo5u7A8" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                style={{ 
                                    color: '#2563eb', 
                                    textDecoration: 'none', 
                                    fontWeight: '600',
                                    fontSize: '1rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}
                            >
                                <MapPin size={18} />
                                Open in Google Maps →
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;

